import { Box, Tooltip } from '@material-ui/core'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import { getGameCodeFromName } from 'lib/utils'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AutoSizer,
  Column,
  SortDirection,
  SortDirectionType,
  Table,
  TableRowRenderer,
} from 'react-virtualized'
import { GAME_NAMES, TAXATION_SCHEME_CONVERSIONS } from 'shared'
import { TransactionLogInterface } from './InventoryTransactionsPresenter'

interface TaxTransactionsTableProps {
  transactions: TransactionLogInterface[]
}

export const TaxTransactionsTable = ({ transactions }: TaxTransactionsTableProps): JSX.Element => {
  const { t } = useTranslation()
  const [sortBy, setSortBy] = useState<string>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.DESC)
  const sort = ({ sortBy }: { sortBy: string }) => {
    setSortBy(sortBy)
    setSortDirection(sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC)
    let sortedOrders = [...transactions]

    if (sortBy === 'createdAt') {
      sortedOrders = transactions.sort((a, b) => {
        return a.createdAt < b.createdAt ? 1 : -1
      })
    } else if (sortBy === 'processedAt') {
      sortedOrders = transactions.sort((a, b) => {
        const from = a.processedAt ?? ''
        const to = b.processedAt ?? ''
        return from < to ? 1 : -1
      })
    }

    if (sortDirection === SortDirection.DESC) {
      sortedOrders = sortedOrders.reverse()
    }
    return sortedOrders
  }

  const getFormattedDate = (d: Date | undefined) => {
    if (d) {
      const localeDate = new Date(d).toLocaleString()
      const [date, time] = localeDate.split(', ')
      return (
        <>
          {date} - {time}
        </>
      )
    } else return <></>
  }

  const rowRenderer: TableRowRenderer = ({ index, key, style, columns, className }) => {
    index
    return (
      <div key={key} style={style} className={className}>
        {columns}
      </div>
    )
  }

  return (
    <div data-testid="tax-transactions-table" className="transactions-table">
      <h5>{t('transaction.taxTransactions')}</h5>
      <div className="mt-3 responsive-table-container">
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table
              width={Math.max(width, 1300)}
              height={transactions.length * 40 + 40}
              headerHeight={40}
              rowHeight={40}
              rowCount={transactions.length}
              rowGetter={({ index }) => transactions[index]}
              rowRenderer={rowRenderer}
              overscanRowCount={5}
              sort={sort}
              sortBy={sortBy}
              sortDirection={sortDirection}
              className="picking-table"
            >
              {/* channel */}
              <Column
                width={150}
                disableSort={true}
                label={t('transaction.channel')}
                dataKey="channel"
                cellRenderer={({ rowData }) => (
                  <Box width="100px" textAlign="center">
                    {rowData.channel}
                  </Box>
                )}
              />

              {/* created at */}
              <Column
                width={150}
                label={t('transaction.createdAt')}
                dataKey="createdAt"
                cellRenderer={({ rowData }) => (
                  <Box width="150px">{getFormattedDate(rowData.createdAt)}</Box>
                )}
              />

              {/* processed at */}
              <Column
                width={170}
                label={t('transaction.processedAt')}
                dataKey="processedAt"
                cellRenderer={({ rowData }) => (
                  <Box width="150px">
                    {rowData.processedAt === null && rowData.lastFailedAt === null ? (
                      <div className="d-flex flex-row align-items-center">
                        <Tooltip title="Pending" placement="top">
                          <span className="processed-icon pending-icon">P</span>
                        </Tooltip>
                      </div>
                    ) : rowData.processedAt === null ? (
                      <div className="d-flex flex-row align-items-center">
                        <Tooltip title="Failed" placement="top">
                          <span className="processed-icon failed-icon">F</span>
                        </Tooltip>
                        {getFormattedDate(rowData.lastFailedAt)}
                      </div>
                    ) : (
                      <div className="d-flex flex-row align-items-center">
                        <Tooltip title="Success" placement="top">
                          <span className="processed-icon success-icon">S</span>
                        </Tooltip>
                        {getFormattedDate(rowData.processedAt)}
                      </div>
                    )}
                  </Box>
                )}
              />

              {/* source */}
              <Column
                width={150}
                disableSort={true}
                label={t('transaction.source')}
                dataKey="source"
                cellRenderer={({ rowData }) => <Box width="100px">{rowData.source}</Box>}
              />

              {/* game */}
              <Column
                width={60}
                disableSort={true}
                label=""
                dataKey="idGame"
                cellRenderer={({ rowData }) => (
                  <span
                    className={`ss-${getGameCodeFromName(
                      GAME_NAMES[rowData.idGame]
                    )} ss-game-icon ss-fw`}
                    title={GAME_NAMES[rowData.idGame]}
                    style={{ fontSize: '1.5rem' }}
                  />
                )}
              />

              {/* name */}
              <Column
                width={300}
                disableSort={true}
                label={t('card.name')}
                dataKey="name"
                cellRenderer={({ rowData }) => (
                  <Box data-testid={rowData.name} width="300px" className="text-wrap">
                    {rowData.name}
                  </Box>
                )}
              />

              {/* cn - expansion */}
              <Column
                width={250}
                disableSort={true}
                label={t('orders.cnExpansion')}
                dataKey="expansionName"
                cellRenderer={({ rowData }) => (
                  <div className="d-flex flex-column">
                    <div className="text-truncate" data-testid={rowData.expansionCode}>
                      #{rowData.cn} - {rowData.expansionCode}
                    </div>
                    <div className="text-truncate" title={rowData.expansionName}>
                      {rowData.expansionName}
                    </div>
                  </div>
                )}
              />

              {/* properties */}
              <Column
                width={200}
                disableSort={true}
                label={t('transaction.properties')}
                dataKey="properties"
                cellRenderer={({ rowData }) => (
                  <Box display="flex" width="120px" alignItems="center">
                    <ArticleAttributes article={rowData} iconHeight={17} />
                  </Box>
                )}
              />

              {/* from - to (tax scheme + qty) */}
              <Column
                width={350}
                disableSort={true}
                label={t('transaction.fromTo')}
                headerClassName="centered-header"
                dataKey="taxFromTo"
                cellRenderer={({ rowData }) => (
                  <Box width="350px" textAlign="center">
                    <div className="d-flex flex-column justify-content-center w-100 align-items-center">
                      <div className="d-flex flex-row">
                        <span>
                          {
                            TAXATION_SCHEME_CONVERSIONS.find(
                              (item) => item.code !== rowData.taxationScheme
                            )?.label
                          }
                          &rArr;
                          {
                            TAXATION_SCHEME_CONVERSIONS.find(
                              (item) => item.code === rowData.taxationScheme
                            )?.label
                          }
                        </span>
                      </div>

                      <div className="d-flex flex-row">
                        <span>{rowData.quantityFrom}</span>
                        &rArr;
                        <span>{rowData.quantityTo}</span>
                        <span className="ms-1">
                          ({rowData.deltaQuantity > 0 ? '+' : ''}
                          {rowData.deltaQuantity})
                        </span>
                      </div>
                    </div>
                  </Box>
                )}
              />
            </Table>
          )}
        </AutoSizer>
      </div>
    </div>
  )
}
